import { useState, useMemo, useRef, useEffect } from 'react';
import { X, Search, Check, ChevronsUpDown } from 'lucide-react';
import { useCreateEnrollment } from '../../api/enrollment/enrollmentQueries';
import { useGetChildren } from '../../api/student/studentQueries';
import { useGetAllSessions } from '../../api/session/sessionQueries';
import { useGetAllStaff } from '../../api/staff/staffQueries';
import { useGetAllSubjects } from '../../api/subject/subjectQueries';
import { useGetAllClasses } from '../../api/class/classQueries';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { LoadingSpinner } from '../ui/loading-spinner.tsx';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialogue.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select.tsx';
import { cn } from '../../styles/formStyles';

export function EnrollmentModal({ open, onOpenChange }) {
  const [formData, setFormData] = useState({
    childId: '',
    subjectId: '',
    sessionId: '',
    classId: '',
    tutorId: '',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  const { data: students = [], isLoading: isLoadingStudents } =
    useGetChildren();
  const { data: sessionsData, isLoading: isLoadingSessions } =
    useGetAllSessions();
  const { data: staffData, isLoading: isLoadingStaff } = useGetAllStaff();
  const { data: subjectsData, isLoading: isLoadingSubjects } =
    useGetAllSubjects();
  const { data: classesData, isLoading: isLoadingClasses } = useGetAllClasses();
  const { mutate: createEnrollment, isPending: isCreating } =
    useCreateEnrollment();

  // Format sessions data
  const sessions = sessionsData?.data || [];

  // Format staff data - filter for teaching staff only
  const tutors = useMemo(() => {
    // For debugging
    console.log('Raw staff data:', staffData);

    // Handle different possible data structures
    const staffArray = Array.isArray(staffData)
      ? staffData
      : Array.isArray(staffData?.data)
      ? staffData.data
      : [];

    return staffArray
      .filter(
        (staff) =>
          staff.role?.name.toLowerCase().includes('teacher') ||
          staff.role?.name.toLowerCase().includes('tutor') ||
          // Include all staff if none match the filter
          staffArray.every(
            (s) =>
              !s.role?.name.toLowerCase().includes('teacher') &&
              !s.role?.name.toLowerCase().includes('tutor')
          )
      )
      .map((staff) => ({
        id: staff.staffId || staff.id,
        name:
          staff.firstName && staff.lastName
            ? `${staff.firstName} ${staff.lastName}`
            : staff.name || staff.fullName || 'Unknown',
      }));
  }, [staffData]);

  // Format subjects data with proper handling of different API response structures
  const subjects = useMemo(() => {
    // For debugging
    console.log('Raw subjects from API:', subjectsData);

    // Handle different possible data structures
    const subjectsArray = Array.isArray(subjectsData)
      ? subjectsData
      : Array.isArray(subjectsData?.data)
      ? subjectsData.data
      : [];

    return subjectsArray.map((subject) => ({
      id: subject.id || subject.subjectId,
      name: subject.subjectName || subject.name,
    }));
  }, [subjectsData]);

  // Format classes data
  const classes = useMemo(() => {
    // For debugging
    console.log('Raw classes from API:', classesData);

    // Handle different possible data structures
    const classesArray = Array.isArray(classesData)
      ? classesData
      : Array.isArray(classesData?.data)
      ? classesData.data
      : [];

    return classesArray.map((classItem) => ({
      id: classItem.classId || classItem.id,
      name: classItem.className || classItem.name || 'Unknown Class',
    }));
  }, [classesData]);

  // Filter students based on search query
  const filteredStudents = useMemo(() => {
    if (!searchQuery.trim()) return [];

    return students.filter((student) =>
      `${student.firstName} ${student.lastName}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  }, [students, searchQuery]);

  // Handle click outside search results
  const handleClickOutside = (event) => {
    if (searchRef.current && !searchRef.current.contains(event.target)) {
      setShowResults(false);
    }
  };

  // Add event listener for click outside
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setSearchQuery(`${student.firstName} ${student.lastName}`);
    handleInputChange('childId', student.childId);
    setShowResults(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    const requiredFields = ['childId', 'sessionId', 'classId'];
    const missingFields = requiredFields.filter((field) => !formData[field]);

    if (missingFields.length > 0) {
      toast.error(
        `Please fill in all required fields: ${missingFields.join(', ')}`
      );
      return;
    }

    createEnrollment(formData, {
      onSuccess: () => {
        onOpenChange(false);
        setFormData({
          childId: '',
          subjectId: '',
          sessionId: '',
          classId: '',
          tutorId: '',
        });
        setSearchQuery('');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to enroll student');
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>New Enrollment</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2" ref={searchRef}>
            <Label htmlFor="student">Student</Label>
            <div className="relative">
              <Input
                id="student"
                type="text"
                placeholder={
                  isLoadingStudents
                    ? 'Loading students...'
                    : 'Search for a student...'
                }
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowResults(true);
                  if (!e.target.value) {
                    setSelectedStudent(null);
                    handleInputChange('childId', '');
                  }
                }}
                onFocus={() => setShowResults(true)}
                disabled={isLoadingStudents}
                className="w-full"
              />
              <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />

              {showResults && filteredStudents.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-200">
                  <ul className="py-1 max-h-60 overflow-auto">
                    {filteredStudents.map((student) => (
                      <li
                        key={student.childId}
                        onClick={() => handleStudentSelect(student)}
                        className={cn(
                          'px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center',
                          selectedStudent?.childId === student.childId &&
                            'bg-gray-50'
                        )}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            selectedStudent?.childId === student.childId
                              ? 'opacity-100'
                              : 'opacity-0'
                          )}
                        />
                        {`${student.firstName} ${student.lastName}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="session">Academic Session</Label>
            <Select
              value={formData.sessionId}
              onValueChange={(value) => handleInputChange('sessionId', value)}
            >
              <SelectTrigger id="session">
                <SelectValue
                  placeholder={
                    isLoadingSessions ? 'Loading sessions...' : 'Select session'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {sessions.map((session) => (
                  <SelectItem key={session.sessionId} value={session.sessionId}>
                    {session.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="class">Class</Label>
            <Select
              value={formData.classId}
              onValueChange={(value) => handleInputChange('classId', value)}
            >
              <SelectTrigger id="class">
                <SelectValue
                  placeholder={
                    isLoadingClasses ? 'Loading classes...' : 'Select class'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {classes.map((classItem) => (
                  <SelectItem key={classItem.id} value={classItem.id}>
                    {classItem.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subjects">Subjects</Label>
            <Select
              value={formData.subjectId}
              onValueChange={(value) => handleInputChange('subjectId', value)}
            >
              <SelectTrigger id="subjects">
                <SelectValue
                  placeholder={
                    isLoadingSubjects
                      ? 'Loading subjects...'
                      : 'Select subjects'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {subjects.map((subject) => (
                  <SelectItem key={subject.id} value={subject.id}>
                    {subject.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tutor">Tutor</Label>
            <Select
              value={formData.tutorId}
              onValueChange={(value) => handleInputChange('tutorId', value)}
            >
              <SelectTrigger id="tutor">
                <SelectValue
                  placeholder={
                    isLoadingStaff ? 'Loading tutors...' : 'Select tutor'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {tutors.map((tutor) => (
                  <SelectItem key={tutor.id} value={tutor.id}>
                    {tutor.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isCreating}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isCreating ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Enrolling...
                </>
              ) : (
                'Enroll Student'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
